import React, { useState, useEffect } from 'react';
import { Customer, CreateCustomerDto, UpdateCustomerDto } from '../../../types';
import { CustomerAddressDto, CreateCustomerAddressDto, UpdateCustomerAddressDto } from '../../../types/locations';
import { CustomerAddressesList } from './CustomerAddressesList';
import { Button } from '@/components/ui/button';
import { Loader2, Map as MapIcon, ChevronDown, ChevronUp } from 'lucide-react';
import { customersApi, customerAddressesApi } from '../../../services/api';
import { Skeleton } from "@/components/ui/skeleton";
import { LocationMap } from '../../../components/LocationMap';
import { EmailListField } from './EmailListField';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

interface CustomerFormProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  customerId?: string | null;
  onSuccess: () => void;
}

const FormSkeleton = () => (
  <div className="space-y-8 animate-pulse">
    <div className="bg-slate-50/50 p-4 rounded-lg border border-slate-100 space-y-4">
      <div className="h-4 w-32 bg-slate-200 rounded" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2"><div className="h-3 w-16 bg-slate-100 rounded" /><div className="h-10 bg-slate-200 rounded-lg" /></div>
        <div className="space-y-2"><div className="h-3 w-16 bg-slate-100 rounded" /><div className="h-10 bg-slate-200 rounded-lg" /></div>
        <div className="space-y-2"><div className="h-3 w-16 bg-slate-100 rounded" /><div className="h-10 bg-slate-200 rounded-lg" /></div>
        <div className="space-y-2"><div className="h-3 w-16 bg-slate-100 rounded" /><div className="h-10 bg-slate-200 rounded-lg" /></div>
      </div>
    </div>
  </div>
);

export const CustomerForm: React.FC<CustomerFormProps> = ({ 
  isOpen, 
  onOpenChange, 
  customerId, 
  onSuccess 
}) => {
  const [formData, setFormData] = useState<Partial<Customer>>({
    name: '',
    externalId: '',
    isActive: true,
    province: '',
    phone: '',
    taxId: '',
    contact: '',
    alias: '',
    email: '',
    zone: '',
    country: '',
    address: '',
    city: '',
    postalCode: '',
    bankAccount01: '',
    bankAccount04: '',
    ibanTransfer: '',
    invoiceType: '',
    invoiceGroup: '',
    electronicInvoice: false,
    electronicInvoiceCode: '',
    series: '',
    paymentMethod: '',
  });

  const [addresses, setAddresses] = useState<CustomerAddressDto[]>([]);
  const [initialAddresses, setInitialAddresses] = useState<CustomerAddressDto[]>([]);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFiscalMap, setShowFiscalMap] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (customerId) {
        loadData();
      } else {
        resetForm();
      }
    }
  }, [isOpen, customerId]);

  const resetForm = () => {
    setFormData({
      name: '',
      externalId: '',
      isActive: true,
      province: '',
      phone: '',
      taxId: '',
      contact: '',
      alias: '',
      email: '',
      zone: '',
      country: '',
      address: '',
      city: '',
      postalCode: '',
      bankAccount01: '',
      bankAccount04: '',
      ibanTransfer: '',
      invoiceType: '',
      invoiceGroup: '',
      electronicInvoice: false,
      electronicInvoiceCode: '',
      series: '',
      paymentMethod: '',
    });
    setAddresses([]);
    setInitialAddresses([]);
    setError(null);
    setShowFiscalMap(false);
  };

  const loadData = async () => {
    try {
      setIsFetching(true);
      setError(null);
      
      const [customer, customerAddresses] = await Promise.all([
        customersApi.getById(customerId!),
        customerAddressesApi.getAll(customerId!)
      ]);

      setFormData({
        ...customer,
        externalId: customer.externalId || '',
        province: customer.province || '',
        phone: customer.phone || '',
        taxId: customer.taxId || '',
        contact: customer.contact || '',
        alias: customer.alias || '',
        email: customer.email || '',
        zone: customer.zone || '',
        country: customer.country || '',
        address: customer.address || '',
        city: customer.city || '',
        postalCode: customer.postalCode || '',
        bankAccount01: customer.bankAccount01 || '',
        bankAccount04: customer.bankAccount04 || '',
        ibanTransfer: customer.ibanTransfer || '',
        invoiceType: customer.invoiceType || '',
        invoiceGroup: customer.invoiceGroup || '',
        electronicInvoice: customer.electronicInvoice || false,
        electronicInvoiceCode: customer.electronicInvoiceCode || '',
        series: customer.series || '',
        paymentMethod: customer.paymentMethod || '',
      });
      setAddresses(customerAddresses);
      setInitialAddresses(customerAddresses);
    } catch (err: any) {
      console.error('Error loading customer data:', err);
      setError(err.message || 'Error al cargar los datos del cliente');
    } finally {
      setIsFetching(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name?.trim()) {
      setError('El nombre es obligatorio');
      return;
    }

    try {
      setIsSubmitting(true);
      const commonData = {
        name: formData.name.trim(),
        isActive: formData.isActive || false,
        province: formData.province || null,
        phone: formData.phone || null,
        taxId: formData.taxId || null,
        contact: formData.contact || null,
        alias: formData.alias || null,
        email: formData.email || null,
        zone: formData.zone || null,
        country: formData.country || null,
        address: formData.address || null,
        city: formData.city || null,
        postalCode: formData.postalCode || null,
        bankAccount01: formData.bankAccount01 || null,
        bankAccount04: formData.bankAccount04 || null,
        ibanTransfer: formData.ibanTransfer || null,
        invoiceType: formData.invoiceType || null,
        invoiceGroup: formData.invoiceGroup || null,
        electronicInvoice: formData.electronicInvoice || false,
        electronicInvoiceCode: formData.electronicInvoiceCode || null,
        series: formData.series || null,
        paymentMethod: formData.paymentMethod || null,
      };
      
      let finalCustomerId = customerId;

      if (customerId) {
        await customersApi.update(customerId, commonData as UpdateCustomerDto);
      } else {
        const newCustomer = await customersApi.create({
          ...commonData,
          externalId: formData.externalId?.trim() || null,
        } as CreateCustomerDto);
        finalCustomerId = newCustomer.id;
      }

      if (finalCustomerId) {
        await syncAddresses(finalCustomerId);
      }

      onSuccess();
    } catch (err: any) {
      console.error('Error saving customer:', err);
      setError(err.message || 'Error al guardar el cliente');
    } finally {
      setIsSubmitting(false);
    }
  };

  const syncAddresses = async (id: string) => {
    const deleted = initialAddresses.filter(ia => !addresses.find(a => a.id === ia.id));
    const added = addresses.filter(a => String(a.id).startsWith('temp-'));
    const updated = addresses.filter(a => 
      initialAddresses.find(ia => ia.id === a.id) && 
      JSON.stringify(a) !== JSON.stringify(initialAddresses.find(ia => ia.id === a.id))
    );

    const promises = [];
    for (const addr of deleted) {
      promises.push(customerAddressesApi.delete(id, addr.id));
    }
    for (const addr of added) {
      const data: CreateCustomerAddressDto = {
        name: addr.name,
        addressLine: addr.addressLine,
        postalCode: addr.postalCode,
        cityId: addr.cityId,
        isDefault: addr.isDefault,
      };
      promises.push(customerAddressesApi.create(id, data));
    }
    for (const addr of updated) {
      const data: UpdateCustomerAddressDto = {
        name: addr.name,
        addressLine: addr.addressLine,
        postalCode: addr.postalCode,
        cityId: addr.cityId,
        isDefault: addr.isDefault,
        isActive: addr.isActive,
      };
      promises.push(customerAddressesApi.update(id, addr.id, data));
    }
    await Promise.all(promises);
  };

  const handleChange = (field: keyof Customer, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 flex flex-col max-w-5xl max-h-[90vh] sm:max-w-5xl min-h-[85vh]">
        <DialogHeader className="p-6 border-b">
          <DialogTitle>{customerId ? 'Editar Cliente' : 'Nuevo Cliente'}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          {isFetching ? (
            <FormSkeleton />
          ) : (
            <>
              <form id="customer-form" onSubmit={handleSubmit} className="space-y-8">
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
                    {error}
                  </div>
                )}

                {/* Info sections */}
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900 mb-4 uppercase tracking-wider">Información Principal</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Nombre *</label>
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => handleChange('name', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-green-500 focus:border-green-500 shadow-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">ID Externo</label>
                      <input
                        type="text"
                        value={formData.externalId || ''}
                        onChange={(e) => handleChange('externalId', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-green-500 focus:border-green-500 shadow-sm disabled:bg-slate-100"
                        disabled={!!customerId}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Alias / Nombre Comercial</label>
                      <input
                        type="text"
                        value={formData.alias || ''}
                        onChange={(e) => handleChange('alias', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-green-500 focus:border-green-500 shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">NIF / CIF</label>
                      <input
                        type="text"
                        value={formData.taxId || ''}
                        onChange={(e) => handleChange('taxId', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-green-500 focus:border-green-500 shadow-sm"
                      />
                    </div>
                    <div className="flex items-center pt-6">
                      <input
                        type="checkbox"
                        id="isActive"
                        checked={formData.isActive}
                        onChange={(e) => handleChange('isActive', e.target.checked)}
                        className="w-4 h-4 text-green-600 border-slate-300 rounded focus:ring-green-500"
                      />
                      <label htmlFor="isActive" className="ml-2 text-sm font-medium text-slate-700">Cliente Activo</label>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900 mb-4 uppercase tracking-wider">Contacto</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Persona de Contacto</label>
                      <input
                        type="text"
                        value={formData.contact || ''}
                        onChange={(e) => handleChange('contact', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-green-500 focus:border-green-500 shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Teléfono</label>
                      <input
                        type="text"
                        value={formData.phone || ''}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-green-500 focus:border-green-500 shadow-sm"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <EmailListField
                        label="Email"
                        value={formData.email || ''}
                        onChange={(v) => handleChange('email', v)}
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">Dirección Fiscal</h3>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setShowFiscalMap(!showFiscalMap)}
                      className="text-xs h-7 gap-1"
                    >
                      <MapIcon className="h-3 w-3" />
                      {showFiscalMap ? 'Ocultar Mapa' : 'Ver Mapa'}
                      {showFiscalMap ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </Button>
                  </div>
                  
                  <div className={`grid grid-cols-1 ${showFiscalMap ? 'lg:grid-cols-2' : ''} gap-6 transition-all`}>
                    <div className="space-y-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-slate-700 mb-1">Dirección</label>
                        <input
                          type="text"
                          value={formData.address || ''}
                          onChange={(e) => handleChange('address', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-green-500 focus:border-green-500 shadow-sm"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Ciudad / Población</label>
                          <input
                            type="text"
                            value={formData.city || ''}
                            onChange={(e) => handleChange('city', e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-green-500 focus:border-green-500 shadow-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Código Postal</label>
                          <input
                            type="text"
                            value={formData.postalCode || ''}
                            onChange={(e) => handleChange('postalCode', e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-green-500 focus:border-green-500 shadow-sm"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {showFiscalMap && (
                      <div className="h-[200px] lg:h-auto overflow-hidden rounded-xl border border-slate-200">
                        <LocationMap 
                          address={formData.address || ''}
                          city={formData.city || ''}
                          province={formData.province || ''}
                          country={formData.country || 'Spain'}
                          postalCode={formData.postalCode || ''}
                          height="100%"
                          zoom={14}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900 mb-4 uppercase tracking-wider">Facturación y Pagos</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Forma de Pago</label>
                      <input
                        type="text"
                        value={formData.paymentMethod || ''}
                        onChange={(e) => handleChange('paymentMethod', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-green-500 focus:border-green-500 shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">IBAN</label>
                      <input
                        type="text"
                        value={formData.ibanTransfer || ''}
                        onChange={(e) => handleChange('ibanTransfer', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-green-500 focus:border-green-500 shadow-sm"
                      />
                    </div>
                    <div className="flex items-center pt-6">
                      <input
                        type="checkbox"
                        id="electronicInvoice"
                        checked={formData.electronicInvoice}
                        onChange={(e) => handleChange('electronicInvoice', e.target.checked)}
                        className="w-4 h-4 text-green-600 border-slate-300 rounded focus:ring-green-500"
                      />
                      <label htmlFor="electronicInvoice" className="ml-2 text-sm font-medium text-slate-700">Factura Electrónica</label>
                    </div>
                  </div>
                </div>
              </form>

              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <CustomerAddressesList 
                  addresses={addresses} 
                  onChange={setAddresses} 
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter className="p-6 border-t bg-slate-50/30">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            form="customer-form"
            disabled={isSubmitting || isFetching}
            className="bg-green-600 hover:bg-green-700 text-white min-w-[140px]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Guardando...
              </>
            ) : (
              <>{customerId ? 'Actualizar' : 'Crear'} Cliente</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
